import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Video, Link2, CheckCircle2, AlertTriangle, Copy, ChevronDown, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` } });

const PROVIDER_COPY = {
  auto: {
    label: "Automatic (recommended)",
    hint: "Uses Google Meet when your calendar is connected, otherwise a private one-off video room per session.",
  },
  google_meet: {
    label: "Google Meet",
    hint: "A real Google Calendar event with its own Meet link. Google emails the invite to the client.",
  },
  static: {
    label: "One fixed room",
    hint: "Every session uses the same link you paste below — your own Meet, Zoom or Teams room.",
  },
  instant_room: {
    label: "Private room per session",
    hint: "A fresh, unguessable browser room for each booking. No accounts, works on mobile.",
  },
};

const SETUP_STEPS = [
  "Go to console.cloud.google.com signed in with your Workspace account (this is NOT admin.google.com — client IDs live in Google Cloud).",
  "Top bar → project picker → New project → name it \"Bad Boss Clinic\" → Create, then make sure it is the selected project.",
  "APIs & Services → Library → search \"Google Calendar API\" → Enable. (Do this first, or the Auth Platform menu stays hidden.)",
  "APIs & Services → Google Auth Platform → Get started → app name \"Bad Boss Clinic\" + your support email.",
  "Audience: choose Internal — you have Workspace, so only your own domain can use it and Google shows no \"unverified app\" warning.",
  "Google Auth Platform → Data access → Add or remove scopes → tick calendar.events and userinfo.email → Update → Save.",
  "Google Auth Platform → Clients → Create client → Application type: Web application → name it \"Bad Boss Clinic backend\".",
  "Authorised redirect URIs → Add URI → paste the URI shown above exactly → Create.",
  "Copy the Client ID and Client secret from the dialog (the secret is only shown once), send both over, then press Connect here.",
];

export const AdminMeetingsCard = () => {
  const [data, setData] = useState(null);
  const [provider, setProvider] = useState("auto");
  const [staticLink, setStaticLink] = useState("");
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showSteps, setShowSteps] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/meetings/settings`, authHeader());
      setData(res.data);
      setProvider(res.data.settings?.provider || "auto");
      setStaticLink(res.data.settings?.static_link || "");
    } catch {
      setData(null);
    }
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const status = params.get("google");
    if (status === "connected") {
      toast.success(`Google Calendar connected${params.get("detail") ? ` — ${params.get("detail")}` : ""}`);
    } else if (status === "error") {
      toast.error(params.get("detail") || "Google Calendar could not be connected");
    }
    if (status) window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/meetings/settings`, { provider, static_link: staticLink }, authHeader());
      toast.success("Meeting settings saved");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save the meeting settings");
    } finally {
      setSaving(false);
    }
  };

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/admin/google/connect`, {}, authHeader());
      window.location.assign(res.data.authorization_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Google is not configured yet");
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? New sessions will fall back to private video rooms.")) return;
    try {
      await axios.post(`${API}/admin/google/disconnect`, {}, authHeader());
      toast.success("Google Calendar disconnected");
      load();
    } catch {
      toast.error("Could not disconnect");
    }
  };

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed — select it manually");
    }
  };

  if (!data) return null;
  const google = data.google || {};

  return (
    <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="admin-meetings-card">
      <h2 className="font-serif text-xl text-white mb-2 flex items-center gap-2">
        <Video className="w-5 h-5 text-[#D4AF37]" />
        Video meetings & calendar invites
      </h2>
      <p className="text-slate-400 text-sm mb-6 max-w-3xl">
        Every paid booking gets a video link in its confirmation email, a calendar invite attached, and the same link
        again in the 24-hour reminder and the client portal.
      </p>

      <div className="border border-slate-800 bg-slate-950/60 p-5 mb-6" data-testid="google-connection-state">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Google Calendar</p>
            {google.connected ? (
              <p className="text-white text-sm flex items-center gap-2" data-testid="google-connected">
                <CheckCircle2 size={15} className="text-emerald-400" />
                Connected as {google.email || "your Google account"}
              </p>
            ) : (
              <p className="text-slate-300 text-sm flex items-center gap-2" data-testid="google-disconnected">
                <AlertTriangle size={15} className="text-[#D4AF37]" />
                {google.configured
                  ? "Credentials are in place — press Connect and approve once."
                  : "Not configured yet: GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET are missing."}
              </p>
            )}
            {google.last_error && !google.connected && (
              <p className="text-red-400 text-xs mt-2 max-w-xl">{google.last_error}</p>
            )}
          </div>
          <div className="flex gap-2">
            {google.connected ? (
              <button
                onClick={disconnect}
                data-testid="google-disconnect-btn"
                className="border border-slate-700 text-slate-300 px-5 py-2.5 text-xs uppercase tracking-widest hover:border-red-500 hover:text-red-400 transition-colors"
              >
                Disconnect
              </button>
            ) : (
              <button
                onClick={connect}
                disabled={connecting || !google.configured}
                data-testid="google-connect-btn"
                className="bg-[#D4AF37] text-slate-950 px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-[#B5952F] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
              >
                {connecting ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />}
                Connect Google Calendar
              </button>
            )}
          </div>
        </div>

        {google.redirect_uri && (
          <div className="mt-5 pt-4 border-t border-slate-800">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">
              Authorised redirect URI (paste into Google Cloud console)
            </p>
            <div className="flex items-center gap-3">
              <code className="text-[#D4AF37] text-xs break-all" data-testid="google-redirect-uri">
                {google.redirect_uri}
              </code>
              <button onClick={() => copy(google.redirect_uri)} className="text-slate-500 hover:text-[#D4AF37]">
                <Copy size={13} />
              </button>
            </div>
          </div>
        )}

        <button
          onClick={() => setShowSteps((s) => !s)}
          data-testid="google-setup-steps-toggle"
          className="mt-5 text-slate-400 text-xs uppercase tracking-widest inline-flex items-center gap-2 hover:text-[#D4AF37] transition-colors"
        >
          <ChevronDown size={13} className={showSteps ? "rotate-180 transition-transform" : "transition-transform"} />
          Google setup steps
        </button>
        {showSteps && (
          <ol className="mt-4 space-y-2 list-decimal list-inside" data-testid="google-setup-steps">
            {SETUP_STEPS.map((step) => (
              <li key={step} className="text-slate-400 text-sm leading-relaxed">{step}</li>
            ))}
          </ol>
        )}
      </div>

      <div className="grid md:grid-cols-[minmax(0,1fr),minmax(0,1fr)] gap-6">
        <div>
          <p className="text-slate-300 text-sm mb-2">How links are created</p>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            data-testid="meeting-provider-select"
            className="w-full bg-slate-950 border border-slate-800 text-white h-12 px-3"
          >
            {Object.entries(PROVIDER_COPY).map(([value, copyText]) => (
              <option key={value} value={value}>{copyText.label}</option>
            ))}
          </select>
          <p className="text-slate-500 text-xs mt-2 leading-relaxed">{PROVIDER_COPY[provider]?.hint}</p>
        </div>
        <div>
          <p className="text-slate-300 text-sm mb-2 flex items-center gap-2">
            <Link2 size={13} className="text-[#D4AF37]" />
            Fixed room link (optional)
          </p>
          <input
            value={staticLink}
            onChange={(e) => setStaticLink(e.target.value)}
            placeholder="https://meet.google.com/abc-defg-hij"
            data-testid="meeting-static-link-input"
            className="w-full bg-slate-950 border border-slate-800 text-white h-12 px-3 text-sm placeholder:text-slate-600"
          />
          <p className="text-slate-500 text-xs mt-2">Used when the provider is set to “One fixed room”.</p>
        </div>
      </div>

      <button
        onClick={save}
        disabled={saving}
        data-testid="save-meeting-settings-btn"
        className="mt-6 bg-[#D4AF37] text-slate-950 px-8 h-12 text-xs uppercase tracking-widest hover:bg-[#B5952F] disabled:opacity-50 transition-colors inline-flex items-center gap-2"
      >
        {saving ? <Loader2 size={14} className="animate-spin" /> : null}
        Save meeting settings
      </button>
    </div>
  );
};
